/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */

// MySiteCare Operations / Monitoring — browser telemetry client.
//
// FAIL-SAFE BY DESIGN: every code path here is wrapped so that a bug in this file, or
// the monitoring endpoint being unreachable, can NEVER break the page it's running on.
// No visitor action should ever fail because MySiteCare's own telemetry failed.
//
// OPT-OUT FOR STANDALONE HANDOVER: this module is only ever imported and initialized
// from App.js's own MySiteCare-managed shell (see initMonitoring() call there). A
// site exported via the Website-Ownership handoff package does not carry App.js's own
// bootstrap code, so it never pulls this file in — monitoring disappearing is the
// expected, correct behavior for a standalone site, not something this file needs to
// guard against itself (see backend/backup_models.py's sibling architecture note and
// the final report's "no kill switch" section for the full reasoning).
//
// PRIVACY: never captures form field values, passwords, or full page content — only
// error metadata (message, stack, route, a pseudonymous per-tab session id).

const STORAGE_KEY = "mysitecare-monitoring-session-id";
const BATCH_INTERVAL_MS = 4000;
const MAX_BATCH_SIZE = 20;
const MAX_STRING_LENGTH = 2000;

let endpoint = null;
let releaseId = "unknown";
let queue = [];
let flushTimer = null;
let initialized = false;

function safe(fn) {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

function getSessionId() {
  return safe(() => {
    let id = window.sessionStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      window.sessionStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  });
}

function currentSiteSlug() {
  return safe(() => {
    const first = window.location.pathname.split("/").filter(Boolean)[0];
    return first && first.endsWith("-concept") ? first : null;
  });
}

function truncate(value) {
  if (typeof value !== "string") return value;
  return value.length > MAX_STRING_LENGTH ? value.slice(0, MAX_STRING_LENGTH) : value;
}

function enqueue(event) {
  safe(() => {
    queue.push(event);
    if (queue.length >= MAX_BATCH_SIZE) {
      flush();
    } else if (!flushTimer) {
      flushTimer = setTimeout(flush, BATCH_INTERVAL_MS);
    }
  });
}

function flush() {
  safe(() => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (!endpoint || queue.length === 0) return;
    const events = queue.slice(0, MAX_BATCH_SIZE);
    queue = queue.slice(MAX_BATCH_SIZE);
    const body = JSON.stringify({ events });

    const sent = safe(() => {
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        return navigator.sendBeacon(endpoint, blob);
      }
      return false;
    });
    if (!sent) {
      safe(() => fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {}));
    }
  });
}

function handleWindowError(event) {
  safe(() => {
    enqueue({
      kind: "js_error",
      site_slug: currentSiteSlug(),
      route: window.location.pathname,
      message: truncate(event?.message || "Unknown error"),
      exception_type: event?.error?.name || "Error",
      stack: truncate(event?.error?.stack || ""),
      session_id: getSessionId(),
      release_id: releaseId,
    });
  });
}

function handleUnhandledRejection(event) {
  safe(() => {
    const reason = event?.reason;
    enqueue({
      kind: "unhandled_rejection",
      site_slug: currentSiteSlug(),
      route: window.location.pathname,
      message: truncate(typeof reason === "string" ? reason : reason?.message || "Unhandled promise rejection"),
      exception_type: reason?.name || "UnhandledRejection",
      stack: truncate(reason?.stack || ""),
      session_id: getSessionId(),
      release_id: releaseId,
    });
  });
}

/**
 * Wraps window.fetch to also report network-level failures (the request never even
 * got a response) — a distinct, valuable signal the backend's own request logging can
 * never see, since it never received anything. Does NOT report ordinary HTTP error
 * STATUSES (4xx/5xx) here — the backend already records those from its own side with
 * far more context; double-reporting the same failure from both ends would just
 * duplicate incidents. This only reports fetch() throwing (DNS failure, connection
 * refused, CORS block, offline, etc.).
 */
function patchFetch() {
  safe(() => {
    if (!window.fetch || window.fetch.__mysitecareMonitoringPatched) return;
    const originalFetch = window.fetch.bind(window);
    const patched = function monitoredFetch(...args) {
      return originalFetch(...args).catch((err) => {
        safe(() => {
          const url = safe(() => (typeof args[0] === "string" ? args[0] : args[0]?.url)) || "unknown";
          enqueue({
            kind: "network_error",
            site_slug: currentSiteSlug(),
            route: window.location.pathname,
            message: truncate(`Request to ${url} failed: ${err?.message || "network error"}`),
            exception_type: err?.name || "NetworkError",
            session_id: getSessionId(),
            release_id: releaseId,
          });
        });
        throw err;
      });
    };
    patched.__mysitecareMonitoringPatched = true;
    window.fetch = patched;
  });
}

/**
 * Call once, as early as possible, from the app's own top-level shell (App.js) —
 * never from an individual page/component. Safe to call multiple times; only the
 * first call does anything.
 */
export function initMonitoring({ apiBase, release } = {}) {
  if (initialized) return;
  safe(() => {
    if (!apiBase) return; // no backend configured (e.g. a fully static preview) — stay silent, never throw
    endpoint = `${apiBase}/monitoring/frontend-events`;
    releaseId = release || process.env.REACT_APP_RELEASE_ID || "unknown";
    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("pagehide", flush);
    patchFetch();
    initialized = true;
  });
}
